import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiMail, FiUserCheck, FiEdit2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import BulkOperations from './BulkOperations';
import EmailComposer from './EmailComposer';
import { usePermissions } from '../../hooks/usePermissions';

const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasPermission } = usePermissions();
  const [filters, setFilters] = useState({ status: '', search: '', priority: '', source: '' });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkOps, setShowBulkOps] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailLead, setEmailLead] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
  }, [filters]);

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const { data } = await axios.get(`/api/crm/leads?${params}`);
      setLeads(data.leads);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`/api/crm/leads/${id}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await axios.put(`/api/crm/leads/${id}`, { status: newStatus });
      toast.success('Status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportToExcel = () => {
    const exportData = leads.map(lead => ({
      'Name': lead.name,
      'Company': lead.company || '-',
      'Email': lead.email || '-',
      'Phone': lead.phone || '-',
      'Status': lead.status,
      'Priority': lead.priority || '-',
      'Source': lead.source || '-',
      'Value': lead.value || 0,
      'Lead Score': lead.leadScore || 0,
      'Owner': lead.owner ? `${lead.owner.firstName || ''} ${lead.owner.lastName || ''}`.trim() : '-',
      'Next Follow-up': lead.nextFollowupAt ? formatDateTime(lead.nextFollowupAt) : '-',
      'Created Date': formatDateTime(lead.createdAt),
      'Notes': lead.notes || '-'
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, `crm-leads-${formatDate(new Date())}.xlsx`);
    toast.success('Exported successfully');
  };

  const handleSelectAll = (checked) => {
    setSelectedLeads(checked ? leads.map(l => l.id) : []);
  };

  const handleSelectLead = (leadId, checked) => {
    setSelectedLeads(checked 
      ? [...selectedLeads, leadId] 
      : selectedLeads.filter(id => id !== leadId)
    );
  };

  const openEmailComposer = (lead) => {
    if (!lead.email) {
      toast.error('This lead has no email address');
      return;
    }
    setEmailLead(lead);
    setShowEmailComposer(true);
  };

  const statusColors = {
    New: 'bg-blue-100 text-blue-800',
    Contacted: 'bg-yellow-100 text-yellow-800',
    Qualified: 'bg-green-100 text-green-800',
    Lost: 'bg-red-100 text-red-800',
    Converted: 'bg-purple-100 text-purple-800'
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/crm')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FiArrowLeft size={20} />
            Back to CRM Dashboard
          </button>
          <h1 className="text-2xl font-bold">CRM Leads</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
          >
            <FiDownload size={16} />
            Export
          </button>
          {hasPermission('crm.create') && (
          <button
            onClick={() => navigate('/crm/leads/new')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + New Lead
          </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search leads..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Statuses</option>
            <option value="New">New</option>
            <option value="Contacted">Contacted</option>
            <option value="Qualified">Qualified</option>
            <option value="Lost">Lost</option>
            <option value="Converted">Converted</option>
          </select>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Sources</option>
            <option value="Website">Website</option>
            <option value="Referral">Referral</option>
            <option value="Social Media">Social Media</option>
            <option value="Email">Email</option>
            <option value="Manual">Manual</option>
          </select>
        </div>
      </div>

      {selectedLeads.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-blue-800 font-medium">
              {selectedLeads.length} lead(s) selected
            </span>
            <button
              onClick={() => setShowBulkOps(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <FiEdit2 size={16} />
              Bulk Operations
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedLeads.length === leads.length && leads.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {leads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                    className="rounded"
                  />
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {lead.name}
                  </button>
                </td>
                <td className="px-6 py-4">{lead.company || '-'}</td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div>{lead.email || '-'}</div>
                    <div className="text-gray-500">{lead.phone || '-'}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[lead.status] || 'bg-gray-100 text-gray-800'}`}>
                    {lead.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-12 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          (lead.leadScore || 0) >= 70 ? 'bg-green-600' :
                          (lead.leadScore || 0) >= 40 ? 'bg-yellow-600' : 'bg-red-600'
                        }`}
                        style={{ width: `${lead.leadScore || 0}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium">{lead.leadScore || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {lead.owner ? `${lead.owner.firstName || ''} ${lead.owner.lastName || ''}`.trim() || lead.owner.email : '-'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {lead.email && (
                      <button
                        onClick={() => openEmailComposer(lead)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Send Email"
                      >
                        <FiMail size={16} />
                      </button>
                    )}
                    {hasPermission('crm.update') && (
                    <button
                      onClick={() => navigate(`/crm/leads/${lead.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    )}
                    {hasPermission('crm.delete') && (
                    <button
                      onClick={() => deleteLead(lead.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      🗑️
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && (
          <div className="text-center py-8 text-gray-500">No leads found</div>
        )}
      </div>

      {showBulkOps && (
        <BulkOperations
          selectedLeads={selectedLeads}
          onComplete={() => {
            setShowBulkOps(false);
            setSelectedLeads([]);
            fetchLeads();
          }}
          onCancel={() => setShowBulkOps(false)}
        />
      )}

      {showEmailComposer && emailLead && (
        <EmailComposer
          leadId={emailLead.id}
          leadEmail={emailLead.email}
          onClose={() => {
            setShowEmailComposer(false);
            setEmailLead(null);
          }}
          onSent={() => fetchLeads()}
        />
      )}
    </div>
  );
};

export default LeadsList;